import clsx from 'clsx'
import { memo, useEffect, useRef, useState } from 'react'

import { Authorization } from '@/widgets/Authorization'

import { useCheckAuth } from '@/features/auth/check'

import { Modal } from '@/shared/ui'

import { BonusConversionForm } from './components/bonus-conversion-form/BonusConversionForm'
import { useWidgetEmbeddedContent } from './hooks/use-widget-embedded-content'
import {
	BONUS_CONVERSION_EMBED_ID,
	BONUS_CONVERSION_TITLE,
	BONUS_CONVERSION_WIDGET_HIDE_DELAY_MS,
	BONUS_CONVERSION_WIDGET_SHOW_DELAY_MS
} from './model/constants'

import cl from './bonus-conversion-widget.module.scss'

export const BonusConversionWidget = memo(() => {
	const { isAuthorized } = useCheckAuth()
	const buttonRef = useRef<HTMLButtonElement>(null)
	const hasInjectedContent = useWidgetEmbeddedContent({ targetRef: buttonRef })
	const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
	const [isFormModalOpen, setIsFormModalOpen] = useState(false)
	const [isWidgetVisible, setIsWidgetVisible] = useState(false)

	const showDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const hideDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => {
		if (showDelayTimerRef.current) {
			clearTimeout(showDelayTimerRef.current)
			showDelayTimerRef.current = null
		}

		if (hideDelayTimerRef.current) {
			clearTimeout(hideDelayTimerRef.current)
			hideDelayTimerRef.current = null
		}

		if (!hasInjectedContent) {
			hideDelayTimerRef.current = setTimeout(() => {
				setIsWidgetVisible(false)
			}, BONUS_CONVERSION_WIDGET_HIDE_DELAY_MS)
			return
		}

		showDelayTimerRef.current = setTimeout(() => {
			setIsWidgetVisible(true)
		}, BONUS_CONVERSION_WIDGET_SHOW_DELAY_MS)

		return () => {
			if (showDelayTimerRef.current) {
				clearTimeout(showDelayTimerRef.current)
				showDelayTimerRef.current = null
			}
		}
	}, [hasInjectedContent])

	useEffect(() => {
		return () => {
			if (showDelayTimerRef.current) {
				clearTimeout(showDelayTimerRef.current)
			}
			if (hideDelayTimerRef.current) {
				clearTimeout(hideDelayTimerRef.current)
			}
		}
	}, [])

	const onOpenAuthModal = () => setIsAuthModalOpen(true)
	const onCloseAuthModal = () => setIsAuthModalOpen(false)
	const onOpenFormModal = () => setIsFormModalOpen(true)
	const onCloseFormModal = () => setIsFormModalOpen(false)

	const handleClick = () => {
		if (!hasInjectedContent) return

		if (isAuthorized) {
			onOpenFormModal()
			return
		}

		onOpenAuthModal()
	}

	const onAuthSuccess = () => {
		onCloseAuthModal()
		onOpenFormModal()
	}

	return (
		<>
			<div className={clsx(cl.rootWrapper, !isWidgetVisible && cl.hidden)}>
				<button
					ref={buttonRef}
					type="button"
					className={clsx(cl.root)}
					aria-label={BONUS_CONVERSION_TITLE}
					disabled={!hasInjectedContent}
					onClick={handleClick}
					data-popmechanic-embed={BONUS_CONVERSION_EMBED_ID}
				/>
			</div>

			<Modal fullScreenOnMobile isOpen={isAuthModalOpen} onClose={onCloseAuthModal}>
				<Authorization onSuccess={onAuthSuccess} />
			</Modal>

			<Modal fullScreenOnMobile isOpen={isFormModalOpen} onClose={onCloseFormModal}>
				<BonusConversionForm onClose={onCloseFormModal} />
			</Modal>
		</>
	)
})
